import { useCallback, useContext, useState } from "react";


import {
  Box,
  Drawer,
  styled,
  Divider,
  useTheme,
  Button,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import LogoutIcon from "@mui/icons-material/Logout";
import { useRecoilValue, useResetRecoilState } from "recoil";

import SidebarMenu from "./SidebarMenu";
import Logo from "@components/LogoSign";
import Scrollbar from "@components/Scrollbar";

import { USER_INFO } from "@states";
import { NoticeModal } from "@components";
import { MAIN_STYLES } from "@constants";
import { resetStorageItem } from "@utils";
import AuthenApi from "@services/auth.api";
import { SidebarContext } from "@contexts/SidebarContext";

const SidebarWrapper = styled(Box)(
  ({ theme }) => `
        width: ${theme.sidebar.width};
        min-width: ${theme.sidebar.width};
        color: ${theme.colors.alpha.trueWhite[70]};
        position: relative;
        z-index: 7;
        height: 100%;
        padding-bottom: 80px;
`
);

function Sidebar() {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useRecoilValue(USER_INFO);
  const closeSidebar = () => toggleSidebar();
  const resetUserState = useResetRecoilState(USER_INFO);
  const [visible, setVisible] = useState<boolean>(false);
  const { sidebarToggle, toggleSidebar } = useContext(SidebarContext);

  const handleLogout = useCallback(async () => {
    await AuthenApi.logout();
    resetUserState();
    resetStorageItem();
    navigate("/");
    // window.location.href = "https://sso-dev-jp.revie.co.kr/logout";
  }, [navigate, resetUserState]);

  return (
    <>
      <SidebarWrapper
        sx={{
          display: {
            lg: "inline-block",
          },
          position: "fixed",
          left: 0,
          top: 0,
          background: "#111111",
          color: "white",
          boxShadow:
            theme.palette.mode === "dark" ? theme.sidebar.boxShadow : "none",
        }}
      >
        <Scrollbar>
          <Box mt={3} mb={"50px"}>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                marginLeft: "30px",
                flexDirection: "column",
              }}
            >
              <Logo />
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "24px",
                  marginTop: "10px",
                  lineHeight: "28px",
                }}
              >
                Admin System
              </Typography>
            </Box>
          </Box>
          <SidebarMenu />
        </Scrollbar>
        <Divider
          sx={{
            background: theme.colors.alpha.trueWhite[10],
          }}
        />
        <Box display="flex" sx={{ pl: 3, py: 1 }}>
          <Box flex={1}>
            <Typography variant="body1" fontSize="1rem">
              {t("sidebar_menu.my_hotel")}
            </Typography>
            <Typography variant="body1" fontSize="0.75rem">
              {user?.email}
            </Typography>
          </Box>
          <Button
            variant="text"
            color="primary"
            startIcon={<LogoutIcon />}
            onClick={() => setVisible(true)}
          />
        </Box>
      </SidebarWrapper>
      <Drawer
        sx={{
          boxShadow: `${theme.sidebar.boxShadow}`,
        }}
        anchor={theme.direction === "rtl" ? "right" : "left"}
        open={sidebarToggle}
        onClose={closeSidebar}
        variant="temporary"
        elevation={9}
      >
        <SidebarWrapper
          sx={{
            background: "#111111",
          }}
        >
          <Scrollbar>
            <Box mt={3}>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Logo />
              </Box>
            </Box>
            <Divider
              sx={{
                mt: theme.spacing(3),
                mx: theme.spacing(2),
                background: theme.colors.alpha.trueWhite[10],
              }}
            />
            <SidebarMenu />
          </Scrollbar>
        </SidebarWrapper>
      </Drawer>
      <NoticeModal
        visible={visible}
        onClose={() => setVisible(false)}
        title={t('messages.mes13')}
        message={t('messages.mes12')}
        buttons={[
          {
            title: t("messages.mes14"),
            onClick: handleLogout,
          },
          {
            title: t("messages.mes15"),
            color: MAIN_STYLES.gray,
            onClick: () => setVisible(false),
          },
        ]}
      />
    </>
  );
}

export default Sidebar;
